// src/interface/ConversationClient.ts
import { ContentType } from "@quromedical/fhir-common";
import { z } from "zod";
var UUIDSchema = z.string().uuid();
var DateStringSchema = z.string().superRefine((value) => {
  return !isNaN(Date.parse(value));
});
var MessageType = /* @__PURE__ */ ((MessageType2) => {
  MessageType2["Text"] = "Text";
  MessageType2["Media"] = "Media";
  return MessageType2;
})(MessageType || {});
var MediaMessageType = /* @__PURE__ */ ((MediaMessageType2) => {
  MediaMessageType2["Image"] = "Image";
  MediaMessageType2["Video"] = "Video";
  MediaMessageType2["Audio"] = "Audio";
  MediaMessageType2["Document"] = "Document";
  return MediaMessageType2;
})(MediaMessageType || {});
var TemplateNames = /* @__PURE__ */ ((TemplateNames2) => {
  TemplateNames2["VideoConsultInvitation"] = "Video Consult Invitation";
  TemplateNames2["ClinicalNoteNotification"] = "ClinicalNoteNotification";
  TemplateNames2["NewPatientAssignment"] = "NewPatientAssignment";
  TemplateNames2["PractitionerWelcomeInvite"] = "PractitionerWelcomeInvite";
  TemplateNames2["PatientMedicationRequest"] = "PatientMedicationRequest";
  TemplateNames2["NewExternalNote"] = "NewExternalNote";
  TemplateNames2["TCPWelcomeMessage"] = "TCPWelcomeMessage";
  TemplateNames2["TCPCollectionDischarge"] = "TCPCollectionDischarge";
  TemplateNames2["TCPCollectionDay8"] = "TCPCollectionDay8";
  TemplateNames2["TCPVitalCollection"] = "TCPVitalCollection";
  TemplateNames2["IntroductoryMessageToOnboardings"] = "Introductory message to onboardings";
  TemplateNames2["NormalVitals"] = "Normal vitals";
  TemplateNames2["AbnormalityInVitalsNoUrgentActionNeeded"] = "Abnormality in vitals no urgent action needed";
  TemplateNames2["AbnormalityInVitalsUrgentActionNeeded"] = "Abnormality in vitals urgent action needed";
  TemplateNames2["MissedVirtualVisit"] = "Missed virtual visit";
  TemplateNames2["PatientMIA"] = "Patient MIA";
  TemplateNames2["NonCompliantWithVirtualVisits"] = "Non compliant with virtual visits";
  TemplateNames2["NotMonitoring"] = "Not monitoring";
  TemplateNames2["TechCalloutRePatching"] = "Tech callout re-patching";
  TemplateNames2["ClinicalCallout"] = "Clinical callout";
  TemplateNames2["QualitySurvey"] = "Quality Survey";
  TemplateNames2["SevenDayReportSurvey"] = "Seven Day Report Survey";
  return TemplateNames2;
})(TemplateNames || {});
var MessageTypeSchema = z.nativeEnum(MessageType);
var ContentTypeSchema = z.nativeEnum(ContentType);
var BaseParticipantSchema = z.object({
  identifier: z.string().describe("The identifier of the participant"),
  name: z.string().optional().describe("The name of the participant"),
  avatar: z.string().url().optional().describe("The avatar of the participant"),
  phoneNumber: z.string().describe("The phone number of the participant")
});
var ParticipantSchema = BaseParticipantSchema.extend({
  id: z.string().describe("The id of the participant")
});
var ReadReceiptSchema = z.object({
  participantId: z.string().describe("The id of the participant"),
  readAt: DateStringSchema
});
var BaseConversationMessageSchema = z.object({
  id: z.string().describe("The id of the message"),
  conversationId: z.string().describe("The id of the conversation"),
  createdAt: DateStringSchema,
  delivered: z.boolean().describe("Whether the message was delivered"),
  read: z.boolean().describe("Whether the message was read"),
  sender: z.string().describe("The sender of the message").optional().default("system")
});
var TextConversationMessageSchema = BaseConversationMessageSchema.extend({
  type: z.literal("Text" /* Text */),
  text: z.string().describe("The text of the message")
});
var MediaConversationSchema = z.object({
  type: z.literal("Media" /* Media */),
  contentType: ContentTypeSchema,
  url: z.string().url().optional().describe("The url of the media"),
  content: z.string().optional().describe("The media content"),
  mediaKey: z.string().optional().describe("The media key"),
  title: z.string().optional().describe("The media title")
});
var MediaConversationMessageSchema = BaseConversationMessageSchema.extend(
  MediaConversationSchema.shape
);
var ConversationMessageSchema = z.discriminatedUnion("type", [
  TextConversationMessageSchema,
  MediaConversationMessageSchema
]);
var BaseCreateTextMessageSchema = z.object({
  type: z.literal("Text" /* Text */),
  text: z.string().describe("The text of the message")
});
var BaseCreateMediaMessageSchema = z.object({
  type: z.literal("Media" /* Media */),
  contentType: ContentTypeSchema.describe("The type of the media"),
  url: z.string().url().optional().describe("The url of the media"),
  text: z.string().optional().describe("The text of the message"),
  content: z.string().optional().describe("The text of the message"),
  title: z.string().describe("The text of the title"),
  mediaKey: z.string().describe("The text of the key")
});
var ParticipantIdSchema = z.string().or(z.literal("system"));
var CreateTextMessageSchema = BaseCreateTextMessageSchema.extend({
  participantId: ParticipantIdSchema
});
var CreateMediaMessageSchema = BaseCreateMediaMessageSchema.extend({
  participantId: ParticipantIdSchema
});
var CreateMessageInputSchema = z.discriminatedUnion("type", [
  CreateTextMessageSchema,
  CreateMediaMessageSchema
]);
var BaseCreateMessageInputSchema = z.discriminatedUnion("type", [
  BaseCreateTextMessageSchema,
  BaseCreateMediaMessageSchema
]);
var ConversationSendMessageSchema = z.object({
  patient: UUIDSchema,
  nextOfKin: UUIDSchema.optional(),
  message: BaseCreateMessageInputSchema
});
var ConversationSendTemplateMessageSchema = z.object({
  patient: UUIDSchema,
  nextOfKin: UUIDSchema.optional(),
  template: z.nativeEnum(TemplateNames)
});
var ConversationSchema = z.object({
  id: z.string().describe("The id of the conversation"),
  participants: ParticipantSchema.array().describe("The participants of the conversation"),
  createdAt: DateStringSchema
});
var GetConversationsInputSchema = z.object({
  limit: z.number().gte(1).lte(100).optional(),
  nextToken: z.string().optional()
});
var GetConversationAlertInputSchema = z.object({
  patient: UUIDSchema.optional()
});
var ConversationListSchema = z.object({
  conversations: ConversationSchema.array(),
  nextToken: z.string().optional()
});
var ConversationSendMessageRequestSchema = ConversationSendMessageSchema.or(
  ConversationSendTemplateMessageSchema
);

// src/types.ts
import { z as z2 } from "zod";

// src/resources.ts
var supportedResources = ["Patient"];
var supportedResourceSet = new Set(supportedResources);

// src/types.ts
var UUIDSchema2 = z2.string().uuid();
var ReferenceSchema = z2.string().refine((value) => {
  const parts = value.split("/");
  if (parts.length !== 2) {
    return false;
  }
  const [resourceType, id] = parts;
  if (!supportedResourceSet.has(resourceType)) {
    return false;
  }
  const parsedId = UUIDSchema2.safeParse(id);
  return parsedId.success;
});
var DisplayReferenceSchema = z2.object({
  reference: ReferenceSchema.optional(),
  display: z2.string().optional()
});
var MessageIdRequestSchema = z2.object({
  messageId: z2.string()
});
var ConversationAlertSchema = z2.object({
  patient: DisplayReferenceSchema,
  alert: z2.boolean(),
  sent: z2.string()
});
var ConversationAlertGetAllResponseSchema = z2.array(ConversationAlertSchema);
export {
  BaseConversationMessageSchema,
  BaseParticipantSchema,
  ConversationAlertGetAllResponseSchema,
  ConversationAlertSchema,
  ConversationMessageSchema,
  ConversationSendMessageSchema,
  ConversationSendTemplateMessageSchema,
  CreateMessageInputSchema,
  GetConversationAlertInputSchema,
  MediaMessageType,
  MessageType,
  ParticipantSchema,
  TemplateNames
};
//# sourceMappingURL=index.js.map